import React, { useState } from "react";
import axios from "axios";
import { AiOutlineClose } from "react-icons/ai";

const formatUser = (u) => {
  if (!u) return "N/A";
  return u.fullName || u.name || u.email || u.phoneNumber || u.id || "N/A";
};

const ReportDetailsModal = ({ report, onClose }) => {
  const [status, setStatus] = useState(report.status);
  const [action, setAction] = useState(
    report.resolution?.action || "NO_ACTION"
  );
  const [adminNotes, setAdminNotes] = useState(report.adminNotes || "");
  const [loading, setLoading] = useState(false);

  const updateReport = async () => {
    try {
      setLoading(true);

      if (status === "UNDER_REVIEW") {
        await axios.patch(
          `/api/v1/app/user-reports/${report._id}/under-review`,
          { adminNotes }
        );
      }

      if (status === "RESOLVED") {
        await axios.patch(
          `/api/v1/app/user-reports/${report._id}/resolve`,
          {
            action,
            duration: 0,
            reason: report.reason,
            adminNotes
          }
        );
      }

      if (status === "DISMISSED") {
        await axios.patch(
          `/api/v1/app/user-reports/${report._id}/dismiss`,
          {
            reason: report.reason,
            adminNotes
          }
        );
      }

      onClose();
      window.location.reload(); // replace with fetchReportData() if available
    } catch (err) {
      console.error("Failed to update report", err);
      alert("Failed to update report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--black)]  flex items-center justify-center px-4">
      <div className=" rounded-lg shadow-xl border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        
        {/* HEADER */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--primary-solid)]">
            Report Details
          </h2>
          <button onClick={onClose}>
            <AiOutlineClose className="text-xl" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="p-6 space-y-6">

          {/* BASIC INFO */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500">Report ID</div>
              <div className="font-mono text-sm">{report._id}</div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Status</div>
              <div className="inline-block px-2 py-1 rounded text-xs bg-[var(--yellow)]">
                {report.status}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Reporter</div>
              <div className="font-medium">{formatUser(report.reporter)}</div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Reported User</div>
              <div className="font-medium">{formatUser(report.reported)}</div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Category</div>
              <div className="font-medium">{report.category}</div>
            </div>

            <div>
              <div className="text-xs text-gray-500">Created At</div>
              <div className="font-medium">
                {new Date(report.createdAt).toLocaleString()}
              </div>
            </div>
          </div>

          {/* REASON */}
          <div>
            <div className="text-xs text-gray-500 mb-1">Reason</div>
            <div className="border rounded p-3 text-sm bg-gray-50">
              {report.reason}
            </div>
          </div>

          {/* EVIDENCE */}
          {Array.isArray(report.evidence) && report.evidence.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 mb-2">Evidence</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {report.evidence.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="border rounded overflow-hidden hover:opacity-80"
                  >
                    <img
                      src={url}
                      alt="Evidence"
                      className="w-full h-32 object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* RESOLUTION INFO */}
          {(report.resolution || report.adminNotes) && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="text-sm font-semibold mb-2">Resolution Info</div>

              {report.resolution?.action && (
                <div className="text-sm">
                  <strong>Action:</strong> {report.resolution.action}
                </div>
              )}

              {report.adminNotes && (
                <div className="text-sm mt-1">
                  <strong>Admin Notes:</strong> {report.adminNotes}
                </div>
              )}
            </div>
          )}

          {/* ADMIN CONTROLS */}
          <div className="border rounded-lg p-4">
            <div className="font-semibold mb-3">Admin Controls</div>

            {/* STATUS */}
            <label className="text-sm block mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-3"
            >
              <option value="PENDING">PENDING</option>
              <option value="UNDER_REVIEW">UNDER REVIEW</option>
              <option value="RESOLVED">RESOLVED</option>
              <option value="DISMISSED">DISMISSED</option>
            </select>

            {/* ACTION */}
            {status === "RESOLVED" && (
              <>
                <label className="text-sm block mb-1">Action</label>
                <select
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-full border rounded px-3 py-2 mb-3"
                >
                  <option value="NO_ACTION">No Action</option>
                  <option value="WARNING">Warning</option>
                  <option value="SUSPENSION">Suspension</option>
                  <option value="BAN">Ban</option>
                </select>
              </>
            )}

            {/* ADMIN NOTES */}
            <label className="text-sm block mb-1">Admin Notes</label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
            />

            {/* UPDATE BUTTON */}
            <div className="flex justify-end mt-4">
              <button
                onClick={updateReport}
                disabled={loading}
                className="px-4 py-2 bg-[var(--primary-solid)] text-white rounded disabled:opacity-50"
              >
                {loading ? "Updating..." : "Update Report"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetailsModal;
